import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  CircularProgress, Chip, Divider, Alert, Stack, LinearProgress,
  InputAdornment, IconButton, Tooltip, Collapse,
} from '@mui/material';
import {
  MyLocation, Search, Navigation, Refresh, Star,
  Visibility, LocationOn, TravelExplore, NightsStay,
  CloudQueue, Brightness3, FilterDrama, ExpandMore, ExpandLess,
  Fullscreen, FullscreenExit,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// ── Fix Leaflet default icon paths broken by webpack ────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ── Custom marker icons ──────────────────────────────────────────────────────
const makeIcon = (color) =>
  L.divIcon({
    className: '',
    html: `<div style="
      width:18px;height:18px;border-radius:50%;
      background:${color};border:2px solid #fff;
      box-shadow:0 0 8px ${color};
    "></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12],
  });

const userIcon = makeIcon('#4fc3f7');

const spotIcon = (bortle) => {
  const colors = {1:'#1a237e',2:'#283593',3:'#1565c0',4:'#039be5',
                  5:'#558b2f',6:'#f9a825',7:'#e65100',8:'#c62828',9:'#b71c1c'};
  return makeIcon(colors[bortle] || '#888');
};

// ── Constants ────────────────────────────────────────────────────────────────
const WEATHER_KEY  = process.env.REACT_APP_OPENWEATHER_API_KEY || '';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min

// ── Bortle scale definitions ─────────────────────────────────────────────────
const BORTLE = {
  1: { label: 'Exceptional Dark Sky',   color: '#1a237e', visibility: 98, emoji: '🌌' },
  2: { label: 'Truly Dark Sky',         color: '#283593', visibility: 88, emoji: '✨' },
  3: { label: 'Rural Sky',              color: '#1565c0', visibility: 74, emoji: '🌠' },
  4: { label: 'Rural/Suburban',         color: '#039be5', visibility: 60, emoji: '⭐' },
  5: { label: 'Suburban Sky',           color: '#558b2f', visibility: 45, emoji: '🔭' },
  6: { label: 'Bright Suburban',        color: '#f9a825', visibility: 30, emoji: '🌆' },
  7: { label: 'Suburban/Urban Transit', color: '#e65100', visibility: 20, emoji: '🌇' },
  8: { label: 'City Sky',              color: '#c62828', visibility: 10, emoji: '🌃' },
  9: { label: 'Inner City Sky',        color: '#b71c1c', visibility: 3,  emoji: '🏙️' },
};

// ── SQM (mag/arcsec²) → Bortle ───────────────────────────────────────────────
function sqmToBortle(sqm) {
  if (sqm >= 21.89) return 1;
  if (sqm >= 21.69) return 2;
  if (sqm >= 21.25) return 3;
  if (sqm >= 20.49) return 4;
  if (sqm >= 19.50) return 5;
  if (sqm >= 18.94) return 6;
  if (sqm >= 18.38) return 7;
  if (sqm >= 17.50) return 8;
  return 9;
}

// ── Moon phase helpers ────────────────────────────────────────────────────────
function moonPhase(date = new Date()) {
  let y = date.getFullYear(), m = date.getMonth() + 1, d = date.getDate();
  if (m < 3) { y--; m += 12; }
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  const jd = Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + d + b - 1524.5;
  const daysSince = (jd - 2451549.5) % 29.53058770576;
  const phase = (daysSince < 0 ? daysSince + 29.53058770576 : daysSince) / 29.53058770576;
  return phase; // 0=new, 0.5=full
}

function moonPhaseName(phase) {
  if (phase < 0.03 || phase > 0.97) return 'New Moon';
  if (phase < 0.22) return 'Waxing Crescent';
  if (phase < 0.28) return 'First Quarter';
  if (phase < 0.47) return 'Waxing Gibbous';
  if (phase < 0.53) return 'Full Moon';
  if (phase < 0.72) return 'Waning Gibbous';
  if (phase < 0.78) return 'Last Quarter';
  return 'Waning Crescent';
}

function moonBrightnessPct(phase) {
  // 0% at New Moon (phase=0), 100% at Full Moon (phase=0.5)
  return Math.round((1 - Math.cos(phase * 2 * Math.PI)) / 2 * 100);
}

function moonEmoji(phase) {
  const idx = Math.round(phase * 7) % 8;
  return ['🌑','🌒','🌓','🌔','🌕','🌖','🌗','🌘'][idx];
}

// ── Generate candidate nearby points ─────────────────────────────────────────
function nearbyPoints(lat, lon) {
  const candidates = [
    { dist: 20, bearing: 0   },
    { dist: 25, bearing: 60  },
    { dist: 30, bearing: 120 },
    { dist: 20, bearing: 180 },
    { dist: 35, bearing: 240 },
    { dist: 28, bearing: 300 },
  ];
  return candidates.map(({ dist, bearing }) => {
    const R  = 6371;
    const br = (bearing * Math.PI) / 180;
    const la = (lat * Math.PI) / 180;
    const lo = (lon * Math.PI) / 180;
    const la2 = Math.asin(
      Math.sin(la) * Math.cos(dist / R) +
      Math.cos(la) * Math.sin(dist / R) * Math.cos(br)
    );
    const lo2 = lo + Math.atan2(
      Math.sin(br) * Math.sin(dist / R) * Math.cos(la),
      Math.cos(dist / R) - Math.sin(la) * Math.sin(la2)
    );
    return { lat: la2 * (180 / Math.PI), lon: lo2 * (180 / Math.PI), distance: dist };
  });
}

// ── Cache helpers ─────────────────────────────────────────────────────────────
function getCached(key) {
  try {
    const raw = localStorage.getItem(`sg_${key}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) { localStorage.removeItem(`sg_${key}`); return null; }
    return data;
  } catch { return null; }
}
function setCache(key, data) {
  try { localStorage.setItem(`sg_${key}`, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

// ── Fetch SQM: direct → CORS proxy → null ────────────────────────────────────
async function fetchSQM(lat, lon) {
  const cacheKey = `sqm_${lat.toFixed(3)}_${lon.toFixed(3)}`;
  const cached = getCached(cacheKey);
  if (cached !== null) return cached;

  const endpoint = `https://www.lightpollutionmap.info/QueryRaster/?ql=wa_2015&qt=point&lng=${lon}&lat=${lat}`;

  // 1. Direct request
  try {
    const res = await fetch(endpoint, { signal: AbortSignal.timeout(5000) });
    if (res.ok) {
      const json = await res.json();
      if (typeof json?.b === 'number') { setCache(cacheKey, json.b); return json.b; }
    }
  } catch { /* try proxy next */ }

  // 2. CORS proxy fallback
  try {
    const res = await fetch(
      `https://corsproxy.io/?${encodeURIComponent(endpoint)}`,
      { signal: AbortSignal.timeout(8000) }
    );
    if (res.ok) {
      const json = await res.json();
      if (typeof json?.b === 'number') { setCache(cacheKey, json.b); return json.b; }
    }
  } catch { /* fall through */ }

  return null; // will trigger address-type estimation
}

// ── Reverse geocode with address details ─────────────────────────────────────
async function reverseGeocodeDetailed(lat, lon) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1`,
      { headers: { 'User-Agent': 'SpacePulse/1.0', 'Accept-Language': 'en' }, signal: AbortSignal.timeout(8000) }
    );
    const data = await res.json();
    const addr = data?.address || {};
    const name =
      addr.village || addr.town || addr.suburb ||
      addr.city    || addr.county || addr.state || 'Your Location';
    // Derive place tier for Bortle estimation
    const tier =
      addr.neighbourhood || addr.suburb ? 'suburb' :
      addr.city          ? 'city'   :
      addr.town          ? 'town'   :
      addr.village       ? 'village': 'rural';
    return { name, tier };
  } catch {
    return { name: 'Your Location', tier: 'city' };
  }
}

// ── Estimate Bortle & SQM from address tier ───────────────────────────────────
function estimateFromTier(tier) {
  // tier → [bortle, sqm]
  const map = {
    suburb:  [8, 17.8],
    city:    [7, 18.2],
    town:    [5, 20.0],
    village: [4, 20.7],
    rural:   [3, 21.2],
  };
  const [bortle, sqm] = map[tier] || [6, 19.0];
  return { bortle, sqm };
}

// ── Fetch weather — Open-Meteo (free, no key) with OpenWeather as optional override
async function fetchWeather(lat, lon) {
  const cacheKey = `wx_${lat.toFixed(2)}_${lon.toFixed(2)}`;
  const cached = getCached(cacheKey);
  if (cached !== null) return cached;

  // 1. OpenWeather (if API key provided)
  if (WEATHER_KEY) {
    try {
      const res  = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${WEATHER_KEY}&units=metric`,
        { signal: AbortSignal.timeout(6000) }
      );
      if (res.ok) {
        const json = await res.json();
        const data = {
          cloudCover:  json?.clouds?.all ?? 0,
          description: json?.weather?.[0]?.description ?? '',
          temp:        Math.round(json?.main?.temp ?? 0),
          humidity:    json?.main?.humidity ?? 0,
          windSpeed:   json?.wind?.speed ?? 0,
        };
        setCache(cacheKey, data);
        return data;
      }
    } catch { /* fall through to Open-Meteo */ }
  }

  // 2. Open-Meteo — completely free, no API key needed
  try {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=cloud_cover,temperature_2m,relative_humidity_2m,wind_speed_10m&wind_speed_unit=ms`,
      { signal: AbortSignal.timeout(8000) }
    );
    if (res.ok) {
      const json = await res.json();
      const c    = json?.current ?? {};
      const data = {
        cloudCover:  c.cloud_cover  ?? 0,
        description: '',
        temp:        Math.round(c.temperature_2m ?? 0),
        humidity:    c.relative_humidity_2m ?? 0,
        windSpeed:   c.wind_speed_10m ?? 0,
      };
      setCache(cacheKey, data);
      return data;
    }
  } catch { /* ignore */ }

  return null;
}

// ── Simple reverse geocode (name only) ───────────────────────────────────────
async function reverseGeocode(lat, lon) {
  const { name } = await reverseGeocodeDetailed(lat, lon);
  return name;
}

// ── Compute combined visibility score ────────────────────────────────────────
function computeScore({ bortle, cloudCover = 0, moonBright = 0 }) {
  const lightScore = ((9 - bortle) / 8) * 100;
  const score = Math.max(0, Math.min(100,
    lightScore - cloudCover * 0.4 - moonBright * 0.25
  ));
  return Math.round(score);
}

function scoreColor(score) {
  if (score >= 70) return '#4caf50';
  if (score >= 45) return '#8bc34a';
  if (score >= 25) return '#ffc107';
  return '#f44336';
}

function scoreLabel(score) {
  if (score >= 70) return 'Excellent';
  if (score >= 45) return 'Good';
  if (score >= 25) return 'Fair';
  return 'Poor';
}

// ── Quality message ───────────────────────────────────────────────────────────
function skyMessage(bortle, cloudCover, moonBright) {
  if (bortle <= 2)  return 'Pristine dark skies! The Milky Way is naked-eye visible with incredible detail.';
  if (bortle <= 4)  return 'Excellent sky conditions. Most deep-sky objects are visible with binoculars.';
  if (bortle === 5) return 'Moderate sky quality. The Milky Way is faintly visible away from the horizon.';
  if (bortle === 6) return 'Sky visibility is limited due to suburban light pollution.';
  if (bortle === 7) return 'Light pollution significantly reduces star count. Only bright stars visible.';
  if (bortle === 8) return 'City skies — only the Moon, planets and ~100 brightest stars visible.';
  return 'Heavy inner-city light pollution. Stargazing is severely hampered.';
}

// ── Map fly-to helper ────────────────────────────────────────────────────────
function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 1.5 });
  }, [center, zoom, map]);
  return null;
}

// ── Create light-pollution pane with screen blend mode ───────────────────────
// mix-blend-mode: screen makes black (dark sky) transparent and lets
// colored city lights show through vividly on the dark base map.
function SetupLPPane() {
  const map = useMap();
  useEffect(() => {
    if (!map.getPane('lpPane')) {
      const pane = map.createPane('lpPane');
      pane.style.zIndex = '450';
      pane.style.mixBlendMode = 'screen';
      pane.style.pointerEvents = 'none';
    }
  }, [map]);
  return null;
}


// ── Global city-light dataset for offline SQM estimation ─────────────────────
// [lat, lon, sqm_at_center, influence_radius_degrees]
// Lower SQM = more light pollution. Gaussian decay applied outward from each city.
const CITY_LIGHTS = [
  // North America
  [40.71,-74.00,16.5,2.5],[34.05,-118.25,16.8,3.0],[41.85,-87.65,17.0,2.0],
  [29.76,-95.37,17.2,1.8],[33.45,-112.07,17.5,1.5],[39.95,-75.17,17.0,1.5],
  [37.77,-122.42,17.2,1.5],[47.61,-122.33,17.5,1.2],[43.65,-79.38,17.3,1.5],
  [45.50,-73.57,17.2,1.2],[49.25,-123.12,17.8,1.0],[19.43,-99.13,17.0,2.0],
  // South America
  [-23.55,-46.63,17.0,2.0],[-22.91,-43.17,17.2,1.8],[-34.60,-58.38,17.3,1.5],
  [-33.46,-70.65,17.8,1.0],[-12.05,-77.04,17.8,1.0],
  // Europe
  [51.51,-0.13,16.8,2.0],[48.85,2.35,16.9,1.8],[52.52,13.40,17.0,1.5],
  [41.01,28.98,17.0,1.5],[55.75,37.62,17.0,2.0],[48.21,16.37,17.5,1.2],
  [52.23,21.01,17.3,1.2],[50.85,4.35,17.2,1.3],[52.37,4.90,17.3,1.2],
  [47.38,8.54,17.5,1.0],[48.14,11.58,17.3,1.3],[51.51,7.47,17.0,1.8],
  [45.46,9.19,17.2,1.3],[41.89,12.51,17.3,1.2],[40.42,-3.70,17.4,1.2],
  [38.72,-9.14,17.8,0.8],[53.40,-2.98,17.2,1.3],[59.33,18.07,17.8,0.8],
  // Middle East & Africa
  [30.06,31.25,17.2,1.5],[31.77,35.22,17.5,1.0],[25.20,55.27,17.3,1.2],
  [24.69,46.72,17.5,1.0],[33.34,44.40,17.5,1.0],[35.69,51.39,17.2,1.5],
  [6.45,3.40,17.5,1.2],[-26.20,28.04,17.3,1.3],[-33.93,18.42,17.8,0.8],
  // South & South-East Asia
  [28.61,77.21,16.8,2.5],[19.08,72.88,17.0,2.0],[13.08,80.27,17.2,1.5],
  [22.57,88.37,17.2,1.5],[12.97,77.59,17.3,1.2],[23.73,90.39,17.3,1.2],
  [13.75,100.52,17.3,1.3],[10.82,106.63,17.5,1.2],[3.15,101.69,17.5,1.0],
  [-6.21,106.85,17.2,1.5],[14.60,120.98,17.4,1.2],
  // East Asia
  [31.23,121.47,16.8,2.5],[39.90,116.40,16.9,2.5],[23.13,113.26,17.0,2.5],
  [30.59,114.31,17.2,1.5],[36.06,103.83,17.8,0.8],[30.67,104.07,17.5,1.0],
  [35.69,139.69,16.7,3.0],[34.69,135.50,17.2,1.8],[37.57,126.98,17.0,1.8],
  [25.05,121.53,17.2,1.3],[22.32,114.17,17.0,1.5],[1.30,103.82,17.2,1.0],
  // Australia
  [-33.87,151.21,17.5,1.2],[-37.81,144.96,17.5,1.0],[-27.47,153.02,17.8,0.8],
];

// Estimate SQM from global city-light dataset (no API, no network call)
function estimateGlobalSQM(lat, lon) {
  let sqm = 21.9; // remote dark-sky baseline
  for (const [cLat, cLon, centerSqm, r] of CITY_LIGHTS) {
    const dist = Math.hypot(lat - cLat, lon - cLon);
    if (dist < r * 5) {
      const effect = (21.9 - centerSqm) * Math.exp(-(dist * dist) / (r * r));
      sqm = Math.min(sqm, 21.9 - effect);
    }
  }
  return Math.max(15.5, sqm);
}



// ════════════════════════════════════════════════════════════════════════════════
export default function StargrazingPage() {
  const [location, setLocation]         = useState(null);      // { lat, lon }
  const [locName, setLocName]           = useState('');
  const [manualCity, setManualCity]     = useState('');
  const [loading, setLoading]           = useState(false);
  const [spotsLoading, setSpotsLoading] = useState(false);
  const [error, setError]               = useState('');

  const [skyData, setSkyData]       = useState(null);   // { sqm, bortle, estimated? }
  const [weather, setWeather]       = useState(null);   // { cloudCover, ... }
  const [moonData, setMoonData]     = useState(null);   // { phase, name, bright }
  const [score, setScore]           = useState(null);
  const [nearbySpots, setNearbySpots] = useState([]);
  const [showMap, setShowMap]           = useState(true);
  const [pollutionLayer, setPollutionLayer] = useState(true);
  const [lpYear, setLpYear]             = useState('2024');
  const [mapCenter, setMapCenter]       = useState([20, 0]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapSectionRef                   = useRef(null);

  // ── Moon data on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    const phase = moonPhase();
    setMoonData({
      phase,
      name:   moonPhaseName(phase),
      bright: moonBrightnessPct(phase),
      emoji:  moonEmoji(phase),
    });
  }, []);

  // ── Fullscreen API ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      // Unlock orientation when fullscreen is exited (e.g. via Esc key)
      if (!active && window.screen.orientation?.unlock) window.screen.orientation.unlock();
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const isMobile = window.innerWidth < 900;
    if (!document.fullscreenElement) {
      await mapSectionRef.current?.requestFullscreen?.();
      // Lock to landscape on mobile so the map fills the screen horizontally
      if (isMobile && window.screen.orientation?.lock) {
        try { await window.screen.orientation.lock('landscape'); } catch { /* permission denied or unsupported */ }
      }
    } else {
      if (window.screen.orientation?.unlock) window.screen.orientation.unlock();
      await document.exitFullscreen?.();
    }
  }, []);

  // ── Auto-detect location on mount ─────────────────────────────────────────
  useEffect(() => {
    detectLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Recompute score whenever inputs change ────────────────────────────────
  useEffect(() => {
    if (!skyData) return;
    setScore(computeScore({
      bortle:     skyData.bortle,
      cloudCover: weather?.cloudCover ?? 0,
      moonBright: moonData?.bright ?? 0,
    }));
  }, [skyData, weather, moonData]);

  // ── Detect browser location ────────────────────────────────────────────────
  const detectLocation = useCallback(() => {
    setError('');
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser. Please enter a location manually.');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setLocation(coords);
        setMapCenter([coords.lat, coords.lon]);
        await loadData(coords);
      },
      () => {
        setLoading(false);
        setError('Location access denied. Please enter a city name or coordinates below.');
      },
      { timeout: 10000 }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle manual location search ─────────────────────────────────────────
  const handleManualSearch = useCallback(async () => {
    if (!manualCity.trim()) return;
    setError('');
    setLoading(true);

    const q = encodeURIComponent(manualCity.trim());
    let coords = null;

    // 1. Try Nominatim
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&addressdetails=1`,
        { signal: AbortSignal.timeout(8000) }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.length) {
          coords = { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
        }
      }
    } catch { /* try next */ }

    // 2. Fallback — Photon (komoot) — no auth, CORS-friendly
    if (!coords) {
      try {
        const res = await fetch(
          `https://photon.komoot.io/api/?q=${q}&limit=1`,
          { signal: AbortSignal.timeout(8000) }
        );
        if (res.ok) {
          const json = await res.json();
          const feat = json?.features?.[0];
          if (feat) {
            const [lon, lat] = feat.geometry.coordinates;
            coords = { lat, lon };
          }
        }
      } catch { /* fall through */ }
    }

    if (!coords) {
      setError('Location not found. Try a different city name or check spelling.');
      setLoading(false);
      return;
    }

    setLocation(coords);
    setMapCenter([coords.lat, coords.lon]);
    await loadData(coords);
  }, [manualCity]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load all data for given coords ────────────────────────────────────────
  const loadData = useCallback(async (coords) => {
    setLoading(true);
    setSkyData(null);
    setNearbySpots([]);
    setError('');

    // Run detailed geocode + SQM + weather in parallel
    const [geo, sqm, wx] = await Promise.all([
      reverseGeocodeDetailed(coords.lat, coords.lon),
      fetchSQM(coords.lat, coords.lon),
      fetchWeather(coords.lat, coords.lon),
    ]);

    setLocName(geo.name);
    setWeather(wx);

    if (sqm !== null) {
      const bortle = sqmToBortle(sqm);
      setSkyData({ sqm: sqm.toFixed(2), bortle, estimated: false });
    } else {
      // Estimate from address type so cards always render
      const est = estimateFromTier(geo.tier);
      setSkyData({ sqm: est.sqm.toFixed(2), bortle: est.bortle, estimated: true });
    }

    setLoading(false);
    loadNearbySpots(coords, geo.tier);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch nearby stargazing spots ─────────────────────────────────────────
  const loadNearbySpots = useCallback(async (coords, baseTier = 'city') => {
    setSpotsLoading(true);
    const points = nearbyPoints(coords.lat, coords.lon);
    const results = await Promise.all(
      points.map(async (p) => {
        const [sqm, geo] = await Promise.all([
          fetchSQM(p.lat, p.lon),
          reverseGeocodeDetailed(p.lat, p.lon),
        ]);

        let bortle, sqmVal, estimated;
        if (sqm !== null) {
          bortle = sqmToBortle(sqm);
          sqmVal = sqm.toFixed(2);
          estimated = false;
        } else {
          // Spots farther from city centre tend to be darker — tier shifts toward rural
          const tierOrder = ['suburb','city','town','village','rural'];
          const baseIdx   = Math.max(0, tierOrder.indexOf(geo.tier));
          // Shift one or two steps darker based on distance
          const shift     = p.distance >= 30 ? 2 : 1;
          const estTier   = tierOrder[Math.min(tierOrder.length - 1, baseIdx + shift)];
          const est       = estimateFromTier(estTier);
          bortle    = est.bortle;
          sqmVal    = est.sqm.toFixed(2);
          estimated = true;
        }
        return {
          ...p,
          name:       geo.name,
          bortle,
          sqm:        sqmVal,
          estimated,
          visibility: BORTLE[bortle].visibility,
          rating:     Math.max(1, Math.min(5, Math.round((10 - bortle) / 2))),
        };
      })
    );
    const sorted = results
      .sort((a, b) => a.bortle - b.bortle)
      .slice(0, 5);
    setNearbySpots(sorted);
    setSpotsLoading(false);
  }, []);

  // ── Google Maps navigation ────────────────────────────────────────────────
  const navigate = (lat, lon) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`, '_blank');
  };

  // ── Find best spot CTA ────────────────────────────────────────────────────
  const findBestSpot = () => {
    if (!nearbySpots.length) return;
    const best = nearbySpots[0];
    setMapCenter([best.lat, best.lon]);
    navigate(best.lat, best.lon);
  };

  // ── Prediction message ────────────────────────────────────────────────────
  const getPrediction = () => {
    if (!moonData) return null;
    const msgs = [];

    // Moon prediction — always available
    if (moonData.phase < 0.1 || moonData.phase > 0.9) {
      msgs.push('New Moon tonight — ideal for deep-sky viewing! Darkest skies of the month.');
    } else if (moonData.phase > 0.4 && moonData.phase < 0.6) {
      msgs.push('Full Moon is up — best to observe after it sets (~4–5 hrs after sunset).');
    } else if (moonData.bright > 60) {
      msgs.push(`Moon is ${moonData.bright}% illuminated. Observe in the pre-dawn hours for darker skies.`);
    } else {
      msgs.push(`Moon is ${moonData.bright}% illuminated (${moonData.name}) — conditions are reasonable.`);
    }

    // Weather prediction — only when API key is set and data is available
    if (weather) {
      if (weather.cloudCover > 70) {
        msgs.push(`Heavy cloud cover (${weather.cloudCover}%) — consider waiting for a clearer night.`);
      } else if (weather.cloudCover > 30) {
        msgs.push(`Partial clouds (${weather.cloudCover}%). Viewing windows may open between cloud gaps.`);
      } else {
        msgs.push(`Clear skies! Cloud cover only ${weather.cloudCover}% — great night to observe.`);
      }
    }

    return msgs;
  };

  const bortleInfo  = skyData ? BORTLE[skyData.bortle] : null;
  const predictions = getPrediction();

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: "'Orbitron', sans-serif", letterSpacing: 1 }}>
            🌃 Light Pollution & Stargazing Map
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Real-time sky quality, light pollution heatmap and best nearby stargazing spots
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<MyLocation />}
            onClick={detectLocation}
            disabled={loading}
            size="small"
            sx={{ borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: '#fff' } }}
          >
            Auto-Detect
          </Button>
          {location && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => loadData(location)}
              disabled={loading}
              size="small"
              sx={{ borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: '#fff' } }}
            >
              Refresh
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Manual Location Input ─────────────────────────────────────────── */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent sx={{ py: '12px !important' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField
              fullWidth
              size="small"
              placeholder="Enter city, region or coordinates (e.g. Mumbai, India)"
              value={manualCity}
              onChange={(e) => setManualCity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                ),
                sx: { bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleManualSearch}
              disabled={loading || !manualCity.trim()}
              sx={{ whiteSpace: 'nowrap', minWidth: 130, bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
            >
              Search
            </Button>
            {nearbySpots.length > 0 && (
              <Button
                variant="contained"
                startIcon={<TravelExplore />}
                onClick={findBestSpot}
                sx={{
                  whiteSpace: 'nowrap', minWidth: 220,
                  background: 'linear-gradient(135deg, #1a237e, #283593)',
                  border: '1px solid rgba(100,140,255,0.4)',
                  '&:hover': { background: 'linear-gradient(135deg, #283593, #3949ab)' },
                }}
              >
                Find Best Spot Near Me
              </Button>
            )}
          </Stack>
          {locName && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              📍 Showing data for: <strong style={{ color: '#fff' }}>{locName}</strong>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Error / loading ───────────────────────────────────────────────── */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(255,167,38,0.1)', border: '1px solid rgba(255,167,38,0.3)' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Main Grid ─────────────────────────────────────────────────────── */}
      {(skyData || loading) && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>

          {/* ── Sky Quality Card ─────────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              height: '100%',
              background: bortleInfo
                ? `linear-gradient(135deg, ${bortleInfo.color}22, rgba(0,0,0,0.6))`
                : 'rgba(255,255,255,0.03)',
              border: bortleInfo
                ? `1px solid ${bortleInfo.color}55`
                : '1px solid rgba(255,255,255,0.1)',
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10 }}>
                    Sky Quality — Bortle Scale
                  </Typography>
                  {skyData?.estimated && (
                    <Chip
                      label="Estimated"
                      size="small"
                      sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(255,167,38,0.12)', color: '#ffa726', border: '1px solid rgba(255,167,38,0.3)' }}
                    />
                  )}
                </Box>

                {loading && !skyData ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Fetching sky data…</Typography>
                  </Box>
                ) : skyData ? (
                  <>
                    {/* Big Bortle badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
                      <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: `radial-gradient(circle, ${bortleInfo.color}88, ${bortleInfo.color}22)`,
                        border: `2px solid ${bortleInfo.color}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexDirection: 'column',
                        boxShadow: `0 0 20px ${bortleInfo.color}44`,
                      }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1, color: '#fff' }}>
                          {skyData.bortle}
                        </Typography>
                        <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>BORTLE</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                          {bortleInfo.emoji} {bortleInfo.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          SQM: {skyData.sqm} mag/arcsec²
                        </Typography>
                      </Box>
                    </Box>

                    {/* Star visibility bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          <Star sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                          Star Visibility
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: bortleInfo.color }}>
                          {bortleInfo.visibility}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={bortleInfo.visibility}
                        sx={{
                          height: 8, borderRadius: 4,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': { bgcolor: bortleInfo.color, borderRadius: 4 },
                        }}
                      />
                    </Box>

                    {/* Message */}
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.5 }}>
                      "{skyMessage(skyData.bortle)}"
                    </Typography>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          {/* ── Combined Score Card ───────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10 }}>
                  Combined Visibility Score
                </Typography>

                {score !== null && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
                      {/* Circular score gauge */}
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CircularProgress
                          variant="determinate"
                          value={score}
                          size={72}
                          thickness={5}
                          sx={{ color: scoreColor(score), filter: `drop-shadow(0 0 8px ${scoreColor(score)}66)` }}
                        />
                        <Box sx={{
                          position: 'absolute', inset: 0,
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1, color: scoreColor(score) }}>
                            {score}
                          </Typography>
                          <Typography sx={{ fontSize: 9, color: 'text.secondary' }}>/100</Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: scoreColor(score) }}>
                          {scoreLabel(score)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Overall sky quality</Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />

                    {/* Score breakdown */}
                    <Stack spacing={1}>
                      {[
                        {
                          icon: <NightsStay sx={{ fontSize: 14 }} />,
                          label: 'Light Pollution',
                          value: skyData ? `Bortle ${skyData.bortle}` : '—',
                          color: bortleInfo?.color || '#888',
                        },
                        {
                          icon: <FilterDrama sx={{ fontSize: 14 }} />,
                          label: 'Cloud Cover',
                          value: weather ? `${weather.cloudCover}%` : '…',
                          color: weather?.cloudCover > 50 ? '#f44336' : '#4caf50',
                        },
                        {
                          icon: <Brightness3 sx={{ fontSize: 14 }} />,
                          label: `Moon — ${moonData?.name}`,
                          value: `${moonData?.bright ?? 0}% ${moonData?.emoji}`,
                          color: moonData?.bright > 70 ? '#f44336' : moonData?.bright > 30 ? '#ffc107' : '#4caf50',
                        },
                      ].map(({ icon, label, value, color }) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, color: 'text.secondary' }}>
                            {icon}
                            <Typography variant="caption">{label}</Typography>
                          </Box>
                          <Chip
                            label={value}
                            size="small"
                            sx={{ height: 20, fontSize: 11, bgcolor: `${color}22`, color, border: `1px solid ${color}44` }}
                          />
                        </Box>
                      ))}
                    </Stack>

                    {/* Score formula note */}
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 1.5, fontSize: 10 }}>
                      Score = Light(40%) − Cloud(40%) − Moon(25%)
                    </Typography>
                  </>
                )}

                {!score && loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Calculating…</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ── Sky Prediction Card ───────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10 }}>
                  Sky Improvement Prediction
                </Typography>

                {predictions ? (
                  <>
                    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                      {predictions.map((msg, i) => (
                        <Box key={i} sx={{
                          p: 1.5, borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          display: 'flex', gap: 1,
                        }}>
                          <Typography sx={{ fontSize: 16 }}>
                            {i === 0 ? moonData?.emoji : '🌤️'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                            {msg}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    {weather && (
                      <>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {[
                            { label: 'Temp',     value: `${weather.temp}°C` },
                            { label: 'Humidity', value: `${weather.humidity}%` },
                            { label: 'Wind',     value: `${weather.windSpeed} m/s` },
                          ].map(({ label, value }) => (
                            <Box key={label} sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}

                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading prediction…</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Interactive Map ────────────────────────────────────────────────── */}
      <Card ref={mapSectionRef} sx={{ mb: 3, bgcolor: '#000', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ pb: '8px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                🗺️ Interactive Light Pollution Map
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                VIIRS satellite data · {lpYear === '2024' ? '⬤ Live (2024, latest)' : `Historical ${lpYear}`} · Black = dark sky · Colors = light pollution intensity
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
              {/* Year selector — historical + live 2024 */}
              {[
                { yr: '2016', label: '2016' },
                { yr: '2020', label: '2020' },
                { yr: '2022', label: '2022' },
                { yr: '2024', label: '⬤ Live', live: true },
              ].map(({ yr, label, live }) => (
                <Button
                  key={yr}
                  size="small"
                  variant={lpYear === yr ? 'contained' : 'outlined'}
                  onClick={() => setLpYear(yr)}
                  sx={{
                    fontSize: 11, minWidth: live ? 62 : 48, px: 1,
                    bgcolor: lpYear === yr
                      ? live ? 'rgba(76,175,80,0.25)' : 'rgba(79,195,247,0.2)'
                      : 'transparent',
                    borderColor: lpYear === yr
                      ? live ? '#4caf50' : '#4fc3f7'
                      : 'rgba(255,255,255,0.2)',
                    color: lpYear === yr
                      ? live ? '#4caf50' : '#4fc3f7'
                      : 'rgba(255,255,255,0.45)',
                    ...(live && lpYear === yr && {
                      '& .MuiButton-startIcon': { animation: 'pulse 1.5s infinite' },
                    }),
                  }}
                >
                  {label}
                </Button>
              ))}
              <Tooltip title={pollutionLayer ? 'Hide overlay' : 'Show overlay'}>
                <Button
                  size="small"
                  variant={pollutionLayer ? 'contained' : 'outlined'}
                  onClick={() => setPollutionLayer(v => !v)}
                  sx={{
                    fontSize: 11,
                    bgcolor: pollutionLayer ? 'rgba(255,255,255,0.15)' : 'transparent',
                    borderColor: 'rgba(255,255,255,0.3)',
                  }}
                >
                  Overlay
                </Button>
              </Tooltip>
              <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                <IconButton
                  size="small"
                  onClick={toggleFullscreen}
                  sx={{
                    color: isFullscreen ? '#4fc3f7' : 'text.secondary',
                    bgcolor: isFullscreen ? 'rgba(79,195,247,0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(79,195,247,0.15)', color: '#4fc3f7' },
                  }}
                >
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
              <Tooltip title={showMap ? 'Minimize map' : 'Show map'}>
                <IconButton size="small" onClick={() => setShowMap(v => !v)} sx={{ color: 'text.secondary' }}>
                  {showMap ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Collapse in={showMap}>
            <Box sx={{ height: isFullscreen ? 'calc(100vh - 110px)' : { xs: 360, md: 520 }, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', transition: 'height 0.3s ease' }}>
              <MapContainer
                center={mapCenter}
                zoom={location ? 7 : 2}
                style={{ height: '100%', width: '100%', background: '#000' }}
                zoomControl
              >
                <FlyTo center={mapCenter} zoom={location ? 7 : 2} />
                <SetupLPPane />

                {/* Layer 1 — pure black base so screen-blend shows city lights only */}
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  maxZoom={19}
                />

                {/* Layer 2 — VIIRS light pollution tiles in screen-blend pane */}
                {pollutionLayer && (
                  <TileLayer
                    key={lpYear}
                    url={`https://djlorenz.github.io/astronomy/image_tiles/tiles${lpYear}/tile_{z}_{x}_{y}.png`}
                    attribution={`VIIRS ${lpYear} &copy; <a href="https://djlorenz.github.io/">D. Lorenz / Colorado School of Mines</a>`}
                    opacity={1}
                    maxNativeZoom={8}
                    maxZoom={19}
                    tileSize={256}
                    pane="lpPane"
                    crossOrigin="anonymous"
                  />
                )}

                {/* Layer 3 — labels on top */}
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png"
                  attribution=""
                  maxZoom={19}
                  pane="shadowPane"
                />

                {/* User location marker */}
                {location && (
                  <>
                    <Marker position={[location.lat, location.lon]} icon={userIcon}>
                      <Popup>
                        <Box sx={{ p: 0.5 }}>
                          <strong>📍 Your Location</strong><br />
                          {locName}<br />
                          {skyData && <>Bortle {skyData.bortle} — {bortleInfo?.label}</>}
                        </Box>
                      </Popup>
                    </Marker>
                    <Circle
                      center={[location.lat, location.lon]}
                      radius={50000}
                      pathOptions={{ color: 'rgba(79,195,247,0.4)', fillColor: 'rgba(79,195,247,0.05)', fillOpacity: 1 }}
                    />
                  </>
                )}

                {/* Nearby spot markers */}
                {nearbySpots.map((spot, i) => (
                  <Marker key={i} position={[spot.lat, spot.lon]} icon={spotIcon(spot.bortle)}>
                    <Popup>
                      <Box>
                        <strong>{spot.name}</strong><br />
                        Bortle {spot.bortle} · {BORTLE[spot.bortle].label}<br />
                        Visibility: {spot.visibility}%<br />
                        Distance: {spot.distance} km<br />
                        <button
                          onClick={() => navigate(spot.lat, spot.lon)}
                          style={{ marginTop: 6, padding: '4px 10px', cursor: 'pointer', borderRadius: 4 }}
                        >
                          Navigate →
                        </button>
                      </Box>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Box>

            {/* Legend */}
            <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 600 }}>
                {lpYear === '2024' ? '⬤ LIVE 2024:' : `VIIRS ${lpYear}:`}
              </Typography>
              {[
                { color: '#000010', label: 'Pristine (Bortle 1–2)' },
                { color: '#1a237e', label: 'Dark rural (3–4)' },
                { color: '#0097a7', label: 'Rural (4–5)' },
                { color: '#388e3c', label: 'Suburban (5–6)' },
                { color: '#f9a825', label: 'Urban (7–8)' },
                { color: '#d32f2f', label: 'City (8–9)' },
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0, border: '1px solid rgba(255,255,255,0.25)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ── Nearby Stargazing Spots ────────────────────────────────────────── */}
      {(nearbySpots.length > 0 || spotsLoading) && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <TravelExplore sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Best Nearby Stargazing Spots
            </Typography>
            {spotsLoading && <CircularProgress size={16} />}
          </Box>

          {spotsLoading && !nearbySpots.length ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map(i => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <CardContent>
                      <LinearProgress sx={{ borderRadius: 1 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                        Scanning nearby areas…
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {nearbySpots.map((spot, i) => {
                const info = BORTLE[spot.bortle];
                return (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card sx={{
                      background: `linear-gradient(135deg, ${info.color}18, rgba(0,0,0,0.5))`,
                      border: `1px solid ${info.color}44`,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 8px 24px ${info.color}33`,
                      },
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                              {spot.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {spot.distance} km away
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            {spot.estimated && (
                              <Chip label="~est" size="small" sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(255,167,38,0.1)', color: '#ffa726' }} />
                            )}
                            <Chip
                              label={`B${spot.bortle}`}
                              size="small"
                              sx={{
                                bgcolor: `${info.color}33`,
                                color: info.color,
                                border: `1px solid ${info.color}66`,
                                fontWeight: 700, fontSize: 11,
                              }}
                            />
                          </Stack>
                        </Box>

                        {/* Visibility bar */}
                        <Box sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              <Visibility sx={{ fontSize: 11, mr: 0.3, verticalAlign: 'middle' }} />
                              Visibility
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: info.color }}>
                              {spot.visibility}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={spot.visibility}
                            sx={{
                              height: 5, borderRadius: 3,
                              bgcolor: 'rgba(255,255,255,0.08)',
                              '& .MuiLinearProgress-bar': { bgcolor: info.color, borderRadius: 3 },
                            }}
                          />
                        </Box>

                        {/* Rating stars */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 0.2 }}>
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star
                                key={n}
                                sx={{ fontSize: 14, color: n <= spot.rating ? '#ffc107' : 'rgba(255,255,255,0.15)' }}
                              />
                            ))}
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {info.label}
                          </Typography>
                        </Box>

                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />

                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<Navigation sx={{ fontSize: 14 }} />}
                          onClick={() => navigate(spot.lat, spot.lon)}
                          sx={{
                            borderColor: `${info.color}66`,
                            color: info.color,
                            fontSize: 12,
                            '&:hover': { borderColor: info.color, bgcolor: `${info.color}11` },
                          }}
                        >
                          Navigate to Spot
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────────── */}
      {!location && !loading && (
        <Card sx={{
          bgcolor: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(255,255,255,0.15)',
          textAlign: 'center', py: 8,
        }}>
          <Typography sx={{ fontSize: 56, mb: 2 }}>🌃</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Discover Your Night Sky
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 400, mx: 'auto' }}>
            Allow location access or enter a city name to see real-time light pollution levels,
            sky quality score and the best nearby spots for stargazing.
          </Typography>
          <Button
            variant="contained"
            startIcon={<MyLocation />}
            onClick={detectLocation}
            sx={{
              background: 'linear-gradient(135deg, #1a237e, #283593)',
              border: '1px solid rgba(100,140,255,0.4)',
              px: 4,
            }}
          >
            Enable Location
          </Button>
        </Card>
      )}

      {/* ── Bortle Scale Reference ───────────────────────────────────────────── */}
      <Card sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', mt: 1 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>
            Bortle Scale Reference
          </Typography>
          <Grid container spacing={1}>
            {Object.entries(BORTLE).map(([level, info]) => (
              <Grid item xs={6} sm={4} md key={level}>
                <Box sx={{
                  p: 1, borderRadius: 1.5, textAlign: 'center',
                  bgcolor: `${info.color}18`,
                  border: `1px solid ${info.color}33`,
                  ...(skyData?.bortle === parseInt(level) && {
                    border: `2px solid ${info.color}`,
                    boxShadow: `0 0 12px ${info.color}44`,
                  }),
                }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: info.color }}>{level}</Typography>
                  <Typography sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1.2 }}>
                    {info.label.split(' ').slice(0, 2).join(' ')}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
